import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { HistoryProjectDialogComponent } from '../history-project-dialog/history-project-dialog.component';
import { DataSet, InvestmentType, Project, ProjectPhase, ProjectsResult } from 'src/app/interfaces/investmentcontrolling/inco-project';
import { FormProjectModalComponent } from '../form-project-modal/form-project-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { DefaultValuesService } from 'src/app/services/defaultvalues.service';
import { InvestmentControllingService } from 'src/app/services/investment-controlling.service';
import { SelectProjectModalComponent } from '../select-project-modal/select-project-modal.component';
import { IncoUserSearchResult } from 'src/app/interfaces/investmentcontrolling/inco-user-search-result';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { TableUtils } from 'src/app/utils/table-utils';

@Component({
  selector: 'app-masterdata-table',
  templateUrl: './masterdata-table.component.html',
  styleUrls: ['./masterdata-table.component.less'],
  standalone: false
})
export class MasterdataTableComponent implements OnInit, OnDestroy {
  @Input() managementId!: number;
  canEdit: boolean = true;
  isEditMode: boolean = false;
  projectsData: Project[] = []
  projectPhases: ProjectPhase[] = [];
  investmentTypes: InvestmentType[] = [];
  multiprojects: DataSet[] = [];
  incoUsers: IncoUserSearchResult[] = [];
  enumSelectors: { key: string, values: { id: number | string | null, label: string }[] }[] = [];
  defaultCategory!: string;

  private readonly destroy$ = new Subject<void>();

  formatPercent(value: number | null | undefined): string {
    return TableUtils.formatDecimal(value, TableUtils.percentDecimals);
  }

  displayedColumns: string[] = ['projectId', 'projectTitle', 'projectResponsible', 'multiproject', 'investmentType', 
    'informationTechnology', 'manufacturing', 'fieldOperations', 'researchDevelopment', 'bigR', 'maintenance', 'expansion', 'focus', 'phase', 'action', 'changeLog'];

  constructor(private readonly dialogForm: MatDialog,
    private readonly toastrService: ToastrService,
    private readonly defaults: DefaultValuesService,
    private readonly incoService: InvestmentControllingService
  ) { }

  ngOnInit(): void {
    this.getProjectPhase();
    this.getInvestmentType();
    this.getMultiprojects();
    this.getIncoUsers();

    this.defaults.managementIdMessageChanges$
      .pipe(debounceTime(50), takeUntil(this.destroy$))
      .subscribe(() => this.loadData());

    if (this.defaults.managementids.length !== 0) {
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getIncoUsers() {
    this.incoService.getIncoUsers(
      this,
      function (tthis: MasterdataTableComponent, result: IncoUserSearchResult[]) {
        tthis.incoUsers = result;
      },
      null
    );
  }

  createProject() {
    const dialogRef = this.dialogForm.open(FormProjectModalComponent, {
      panelClass: ['custom-mat-dialog_v2', 'dialogNgselect'],
      disableClose: true,
      width: '700px',
      data: {
        managementId: this.defaults.managementid,
        isEditMode: false,
        investmentTypes: this.investmentTypes,
        projectPhases: this.projectPhases,
        multiprojects: this.multiprojects,
        defaultCategory: this.defaultCategory,
        incoUsers: this.incoUsers
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.incoService.addMasterData(
          result,
          this,
          function (tthis: MasterdataTableComponent, result: any) {
            setTimeout(() => {
              tthis.toastrService.success('Project created successfully!');
              tthis.reloadData();
            }, 50)
          }
          , function (tthis: MasterdataTableComponent, error: string) {
            tthis.toastrService.error('Cannot upload the project. Please, contact with administrator');
          }
        );
      }
    });
  }

  updateProject(projectId: number) {
    const project = this.projectsData.find(x => x.id == projectId);
    const dialogRef = this.dialogForm.open(FormProjectModalComponent, {
      panelClass: ['custom-mat-dialog_v2', 'dialogNgselect'],
      disableClose: true,
      width: '700px',
      data: {
        managementId: this.defaults.managementid,
        isEditMode: true,
        project: project,
        investmentTypes: this.investmentTypes,
        projectPhases: this.projectPhases,
        multiprojects: this.multiprojects,
        incoUsers: this.incoUsers
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.incoService.updateMasterData(
          result,
          this,
          function (tthis: MasterdataTableComponent, result: any) {
            setTimeout(() => {
              tthis.toastrService.success('Project saved successfully!');
              tthis.reloadData();
            }, 50)
          }
          , function (tthis: MasterdataTableComponent, error: string) {
            tthis.toastrService.error('Cannot upload the project. Please, contact with administrator');
          }
        );
      }
    });
  }

  editProject() {
    const dialogRef = this.dialogForm.open(SelectProjectModalComponent, {
      panelClass: ['custom-mat-dialog_v2', 'dialogNgselect'],
      disableClose: true,
      width: '600px',
      data: {
        projectList: this.projectsData
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.updateProject(result.projectId);
      }
    });
  }

  getBooleanDisplay(value: boolean): string {
    return value ? 'YES' : 'NO';
  }

  cancel() {
    this.isEditMode = false;
  }

  viewprojectHistory(project: any) {
    this.incoService.getProjectHistory(
      project.id,
      this,
      (tthis: MasterdataTableComponent, history: any[]) => {
        tthis.dialogForm.open(HistoryProjectDialogComponent, {
          width: '1400px',
          data: {
            id: project.id,
            projectTitle: project.projectTitle,
            history: history,
            investmentTypes: this.investmentTypes,
            projectPhases: this.projectPhases,
            multiprojects: this.multiprojects
          }
        });
      },
      (tthis: MasterdataTableComponent, error: any) => {
        tthis.toastrService.error('No se pudo obtener el historial del proyecto.');
      }
    );
  }

  loadData() {
    this.incoService.loadMasterData(this, function (tthis: MasterdataTableComponent, result: ProjectsResult) {
      setTimeout(() => {
        tthis.projectsData = result.projects;
        tthis.defaultCategory = result.categoryConfig?.category
      }, 50)
    }
      , null)
  }

  reloadData() {
    this.incoService.getMultiprojects(
      this,
      (tthis: MasterdataTableComponent, result: DataSet[]) => {
        tthis.multiprojects = result;
        const idx = tthis.enumSelectors.findIndex(e => e.key === 'multiproject');
        if (idx === -1) {
          tthis.enumSelectors.push({
            key: 'multiproject',
            values: tthis.multiprojects.map(item => ({ id: item.id, label: item.name }))
          });
        } else {
          tthis.enumSelectors[idx].values = tthis.multiprojects.map(item => ({ id: item.id, label: item.name }));
        }
        tthis.loadData();
      },
      null
    );
  }

  getProjectPhase() {
    this.incoService.getProjectPhase(
      this,
      function (tthis: MasterdataTableComponent, result: ProjectPhase[]) {
        tthis.projectPhases = result;
        tthis.enumSelectors.push({
          key: 'phase',
          values: tthis.projectPhases.map(item => ({ id: item.id, label: item.phaseName }))
        });
      },
      null
    );
  }

  getInvestmentType() {
    this.incoService.getInvestmentType(
      this,
      function (tthis: MasterdataTableComponent, result: InvestmentType[]) {
        tthis.investmentTypes = result;
        tthis.enumSelectors.push({
          key: 'investmentType',
          values: tthis.investmentTypes.map(item => ({ id: item.id, label: item.investmentName }))
        });
      },
      null
    );
  }

  getMultiprojects() {
    this.incoService.getMultiprojects(
      this,
      function (tthis: MasterdataTableComponent, result: DataSet[]) {
        tthis.multiprojects = result;
        tthis.enumSelectors.push({
          key: 'multiproject',
          values: tthis.multiprojects.map(item => ({ id: item.id, label: item.name }))
        });
      },
      null
    );
  }

  getEnumLabel(key: string, id: number) {
    const enumSelector = this.enumSelectors.find(es => es.key === key);
    if (enumSelector) {
      const enumValue = enumSelector.values.find(ev => ev.id === id);
      return enumValue ? enumValue.label : id;
    }
    return id;
  }

  getPhaseLabel(phaseId: number | undefined): string {
    const phase = this.projectPhases.find(p => p.id === phaseId);
    return phase ? `${phaseId} - ${phase.phaseName}` : `${phaseId}`;
  }
}
